import {
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {CurrencyResponseBody} from '@dms/api/shared';
import {
  GetDeliveryNoteResponse,
  GetWarehousesResponse,
  PatchDeliveryNoteForCompletedRequest,
  useLazyGetWarehouseQuery,
  useLazyGetWarehousesQuery,
  usePatchDeliveryNoteForCompletedMutation,
  usePatchDeliveryNoteMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromCurrencies,
  getOptionsFromCustomersAsSupplier,
  getOptionsFromWarehouses,
  handleApiError,
} from '@dms/shared';
import {getOptionsFromPaymentTypes} from '@dms/warehouse/shared';

import {ApiError, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DeliveryNoteForm, DeliveryNoteFormSchema} from '../types/DeliveryNoteForm';

export interface BasicInformationFormProps extends RequiredTestIdProps {
  isCompleted: boolean;
  deliveryNote: GetDeliveryNoteResponse | Nullish;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes?: PaymentTypeResponseBody[] | Nullish;
  currencies?: CurrencyResponseBody[] | Nullish;
  onClose: () => void;
}

export function BasicInformationForm(props: BasicInformationFormProps) {
  const [availableWarehousesToSupplier, setAvailableWarehousesToSupplier] = useState<
    GetWarehousesResponse | Nullish
  >(props.warehouses);

  const [patchDeliveryNote] = usePatchDeliveryNoteMutation();
  const [patchDeliveryNoteForCompleted] = usePatchDeliveryNoteForCompletedMutation();
  const [getWarehouse, {isFetching: isWarehouseFetching}] = useLazyGetWarehouseQuery();
  const [getWarehouses, {isFetching: areWarehousesFetching}] = useLazyGetWarehousesQuery();

  const isDeliveryNoteReceived = props.deliveryNote?.state === 'RECEIVED';
  const isDeliveryNotePending = props.deliveryNote?.state === 'PENDING';
  const isFromReceiveNote = props.deliveryNote?.isFromReceiveNote;

  const supplierId = props.deliveryNote?.supplierId;
  const authorizationProfileId = props.deliveryNote?.authorizationProfileId;

  useEffect(
    function fetchSupplierWarehouses() {
      if (isDeliveryNotePending && supplierId && authorizationProfileId) {
        getWarehouses({
          supplierId,
          authorizationProfileId,
        })
          .unwrap()
          .then(setAvailableWarehousesToSupplier)
          .catch(handleApiError);
      } else {
        return;
      }
    },
    [isDeliveryNotePending, supplierId, authorizationProfileId, getWarehouses]
  );

  const defaultValues: Partial<DeliveryNoteForm> = {
    supplierId: props.deliveryNote?.supplierId,
    warehouseId: props.deliveryNote?.warehouseId,
    authorizationProfileId: props.deliveryNote?.authorizationProfileId,
    invoiceNumber: props.deliveryNote?.invoiceNumber,
    invoiceIssueDate: parseDate(props.deliveryNote?.invoiceIssueDate),
    paymentType: props.deliveryNote?.paymentType,
    currency: props.deliveryNote?.currency,
    exchangeRate: props.deliveryNote?.exchangeRate,
    ncConversion: props.deliveryNote?.ncConversion,
    deliveryNoteNumber: props.deliveryNote?.deliveryNoteNumber,
    issuedAt: parseDate(props.deliveryNote?.issuedAt),
    note: props.deliveryNote?.note,
  };

  const handleSubmit: FormSubmitHandler<DeliveryNoteForm> = async (
    formValues,
    setErrors,
    reset,
    blockerContext
  ) => {
    if (isNil(props.deliveryNote)) {
      return;
    }

    if (props.isCompleted) {
      const requestBody: PatchDeliveryNoteForCompletedRequest['body'] = {
        invoiceNumber: formValues.invoiceNumber,
        invoiceIssueDate: formValues.invoiceIssueDate?.toISOString(),
        deliveryNoteNumber: formValues.deliveryNoteNumber,
        issuedAt: formValues.issuedAt?.toISOString(),
        note: formValues.note,
      };

      await patchDeliveryNoteForCompleted({
        deliveryNoteId: props.deliveryNote.deliveryNoteId,
        body: requestBody,
      })
        .unwrap()
        .then(props.onClose)
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    } else {
      await patchDeliveryNote({
        deliveryNoteId: props.deliveryNote.deliveryNoteId,
        body: {
          ...formValues,
          issuedAt: formValues.issuedAt?.toISOString(),
        },
      })
        .unwrap()
        .then(props.onClose)
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }
  };

  return (
    <Form<DeliveryNoteForm>
      experimentalZodSchema={DeliveryNoteFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => {
        const handleWarehouseChange = (newWarehouseId: string | string[] | number | null) =>
          getWarehouse({warehouseId: newWarehouseId as string})
            .unwrap()
            .then((warehouse) => {
              formApi.setValue('authorizationProfileId', warehouse.authorizationProfileId, {
                shouldValidate: true,
              });
            })
            .catch(handleApiError);

        return (
          <>
            <VStack spacing={4}>
              <Grid columns={4}>
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled
                  control={control}
                  type="choice"
                  name="supplierId"
                  label={i18n.t('entity.warehouse.labels.supplier')}
                  options={getOptionsFromCustomersAsSupplier(props.suppliers)}
                  data-testid={suffixTestId('inputs.supplierId', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote}
                  isLoading={areWarehousesFetching}
                  control={control}
                  type="choice"
                  name="warehouseId"
                  label={i18n.t('entity.warehouse.labels.warehouse')}
                  options={getOptionsFromWarehouses(availableWarehousesToSupplier)}
                  onChange={handleWarehouseChange}
                  data-testid={suffixTestId('inputs.warehouseId', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled
                  isLoading={isWarehouseFetching}
                  control={control}
                  type="choice"
                  name="authorizationProfileId"
                  label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                  options={getOptionsFromAuthorizationProfiles(
                    props.authorizationProfiles,
                    formApi.watch('authorizationProfileId')
                  )}
                  data-testid={suffixTestId('inputs.authorizationProfileId', props)}
                />
              </Grid>
              <Grid columns={4}>
                <FormField
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote && props.isCompleted}
                  control={control}
                  type="text"
                  name="deliveryNoteNumber"
                  label={i18n.t('entity.warehouse.labels.deliveryNoteNumber')}
                  data-testid={suffixTestId('inputs.deliveryNoteNumber', props)}
                />
                <FormField
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote && props.isCompleted}
                  control={control}
                  type="date"
                  name="issuedAt"
                  label={i18n.t('entity.warehouse.labels.deliveryNoteIssueDate')}
                  data-testid={suffixTestId('inputs.issuedAt', props)}
                />
                <FormField
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote && props.isCompleted}
                  control={control}
                  type="text"
                  name="invoiceNumber"
                  label={i18n.t('entity.warehouse.labels.invoiceNo')}
                  data-testid={suffixTestId('inputs.invoiceNo', props)}
                />
                <FormField
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote && props.isCompleted}
                  control={control}
                  type="date"
                  name="invoiceIssueDate"
                  label={i18n.t('entity.warehouse.labels.invoiceIssueDate')}
                  data-testid={suffixTestId('inputs.invoiceIssueDate', props)}
                />
              </Grid>
              <Grid columns={4}>
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote}
                  type="choice"
                  name="paymentType"
                  label={i18n.t('entity.warehouse.labels.paymentType')}
                  options={getOptionsFromPaymentTypes(props.paymentTypes)}
                  control={control}
                  data-testid={suffixTestId('inputs.paymentType', props)}
                />
                <FormField
                  isRequired
                  isNotClearable
                  isDisabled={isDeliveryNoteReceived && isFromReceiveNote}
                  control={control}
                  type="choice"
                  name="currency"
                  label={i18n.t('entity.warehouse.labels.currency')}
                  options={getOptionsFromCurrencies(props.currencies)}
                  data-testid={suffixTestId('inputs.currency', props)}
                />
                <FormField
                  isDisabled
                  control={control}
                  type="text"
                  name="exchangeRate"
                  label={i18n.t('entity.warehouse.labels.exchangeRate')}
                  helperText="Not implemented"
                  data-testid={suffixTestId('inputs.exchangeRate', props)}
                />
                <FormField
                  isDisabled
                  control={control}
                  type="choice"
                  name="ncConversion"
                  label={i18n.t('entity.warehouse.labels.ncConversion')}
                  options={[]}
                  helperText="Not implemented"
                  data-testid={suffixTestId('inputs.ncConversion', props)}
                />
              </Grid>
              <Grid columns={4}>
                <GridItem span={4}>
                  <FormField
                    control={control}
                    type="textarea"
                    name="note"
                    label={i18n.t('entity.warehouse.labels.note')}
                    data-testid={suffixTestId('inputs.note', props)}
                  />
                </GridItem>
              </Grid>
            </VStack>

            <Separator />

            <HStack justify="flex-end">
              <ButtonGroup>
                <Show when={isNotNil(props.deliveryNote)}>
                  <FormButton
                    control={control}
                    type="reset"
                    variant="secondary"
                    title={i18n.t('general.labels.discard')}
                    onClick={props.onClose}
                    data-testid={suffixTestId('actions.discard', props)}
                  />
                </Show>
                <FormButton
                  control={control}
                  type="submit"
                  variant="primary"
                  title={i18n.t('general.labels.saveChanges')}
                  data-testid={suffixTestId('actions.submit', props)}
                />
              </ButtonGroup>
            </HStack>
          </>
        );
      }}
    </Form>
  );
}
